import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, LoadingBlock, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MessagesSquare, Hash, User, Send, Trash2, Flag } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

export default function Conversations() {
  const [convs, setConvs] = useState<any[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => {
    api.get("/conversations").then((r) => { const c = r.data.conversations || []; setConvs(c); if (!active && c[0]) setActive(c[0].id); })
      .catch(() => setConvs([])).finally(() => setLoading(false));
  }, [active]);
  useEffect(() => { load(); }, []);  // eslint-disable-line
  if (loading) return <LoadingBlock />;
  return (
    <div data-testid="conversations">
      <PageHeader title="Conversations" subtitle="Scoped team channels and direct messages" testId="conversations-header" />
      <div className="grid gap-3 md:grid-cols-[260px_1fr]">
        <Card className="p-2" data-testid="conv-list">
          {convs.length === 0 ? <EmptyState icon={<MessagesSquare size={24} />} title="No conversations" description="You have no channels or DMs yet." /> :
            convs.map((c) => (
              <button key={c.id} onClick={() => setActive(c.id)} data-testid={`conv-item-${c.id}`}
                className={`flex w-full items-center justify-between rounded-md px-2.5 py-2 text-left text-sm transition-colors ${active === c.id ? "bg-brand/10 text-brand" : "hover:bg-muted"}`}>
                <span className="flex items-center gap-2 truncate">{c.kind === "channel" ? <Hash size={14} /> : <User size={14} />}<span className="truncate">{c.title}</span></span>
                {c.unread > 0 && <span className="ml-1 rounded-full bg-brand px-1.5 text-[10px] text-white" data-testid={`conv-unread-${c.id}`}>{c.unread}</span>}
              </button>
            ))}
        </Card>
        {active ? <Thread key={active} conversationId={active} onSent={load} /> : <Card className="p-6"><EmptyState icon={<MessagesSquare size={24} />} title="Select a conversation" /></Card>}
      </div>
    </div>
  );
}

const Thread: React.FC<{ conversationId: string; onSent: () => void }> = ({ conversationId, onSent }) => {
  const {
    t
  } = useI18n();

  const [data, setData] = useState<any>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);
  const load = useCallback(() => {
    api.get(`/conversations/${conversationId}/messages`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, [conversationId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [data]);
  const send = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/conversations/${conversationId}/messages`, { body: text.trim() }); setText(""); load(); onSent(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed to send"); }
    finally { setBusy(false); }
  };
  const del = async (id: string) => { try { await api.post(`/conversations/messages/${id}/delete`, {}); toast.success("Message removed"); load(); } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const report = async (id: string) => { try { await api.post(`/conversations/messages/${id}/report`, { reason: "flagged" }); toast.success("Reported to moderators"); load(); } catch { toast.error("Failed"); } };
  if (!data) return <Card className="p-4"><LoadingBlock /></Card>;
  if (data.error) return <Card className="p-4"><EmptyState icon={<MessagesSquare size={22} />} title="Unavailable" /></Card>;
  return (
    (<Card className="flex h-[60vh] flex-col p-0" data-testid="conv-thread">
      <div className="flex-1 space-y-2 overflow-auto p-4">
        {data.messages.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground">{t("No messages yet — say hello.")}</div> :
          data.messages.map((m: any) => (
            <div key={m.id} className={`group flex ${m.is_me ? "justify-end" : "justify-start"}`} data-testid={`conv-msg-${m.id}`}>
              <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.deleted ? "bg-muted italic text-muted-foreground" : m.is_me ? "bg-brand text-white" : "bg-muted"}`}>
                {!m.is_me && !m.deleted && <div className="mb-0.5 text-[11px] font-semibold opacity-70">{m.sender_name}</div>}
                <div>{m.body}</div>
                {!m.deleted && (
                  <div className="mt-1 flex items-center gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                    {(m.is_me || data.can_moderate) && <button onClick={() => del(m.id)} title={t("Remove")} data-testid={`conv-del-${m.id}`}><Trash2 size={12} /></button>}
                    {!m.is_me && <button onClick={() => report(m.id)} title={t("Report")} data-testid={`conv-report-${m.id}`}><Flag size={12} /></button>}
                  </div>
                )}
              </div>
            </div>
          ))}
        <div ref={endRef} />
      </div>
      <div className="flex items-center gap-2 border-t border-border p-3">
        <Input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("Type a message…")} data-testid="conv-input" />
        <Button size="icon" onClick={send} disabled={busy} data-testid="conv-send"><Send size={16} /></Button>
      </div>
      <div className="px-3 pb-2 text-[10px] text-muted-foreground">{t("Messages retained")}{" "}{data.retention_days}{" "}{t("days · membership derived from your store scope")}</div>
    </Card>)
  );
};
